import _ from 'lodash'
import {
	Component,
	Spx,
	Style,
	InnerElementStyle,
	ResolvedInnerElementsDataItem,
	WidgetRefDataItem,
	InnerElementsMap,
	ResolvedBuilderWidgetDataItem,
} from '@wix/thunderbolt-becky-types'
import { getStyleCssVars, stylePropsToCssVars, oldStylePropsToCssVars } from '@wix/thunderbolt-becky-root'
import { createCssCompNode } from '../cssCompNode'
import { FeatureRefs } from '../cssFeatures.types'
import { mapValuesValues } from '../../utils/mapValuesValues'
import { mapValues } from '../../utils/mapValues'
import { envRefs, themeRefs, VariantRelationViewItem } from '@wix/thunderbolt-catharsis'
import { clearFutileOverridesFromCssObject } from '../../utils/futileCssOptimizer'
import { toBreakpointVariantsMap } from '../../utils/toBreakpointVariantsMap'
import { isRefArray } from '../../utils/variantsUtils'
import { createEmptyStyleItem } from './compStylesCompNode'

const COMP_TYPES_WITH_SPECIFIC_VARS = new Set([
	'TPASection',
	'TPAMultiSection',
	'TPAWidget',
	'TPAGluedWidget',
	'tpaWidgetNative',
	'SlideShowSlide',
])

const styleToCssVars = (
	style: Style,
	siteColors: Array<string>,
	isScalePropExperimentOn: boolean,
	themeSpx: Spx | undefined,
	componentsSkinParams: any
) => {
	if (!style?.style?.properties || 'st-css' in style.style.properties) {
		return undefined
	}
	return getStyleCssVars(style, isScalePropExperimentOn, themeSpx, siteColors, componentsSkinParams)
}

const compThemeCompNode = createCssCompNode('styleBased', 'compTheme', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) =>
		COMP_TYPES_WITH_SPECIFIC_VARS.has(component.componentType)
			? null
			: {
					compStyles: refs.compStyles(component),
					sitesEditorName: envRefs.editorName,
					theme: themeRefs.theme,
					experiments: envRefs.experiments,
					siteColors: themeRefs.siteColors,
					componentsSkinParams: envRefs.componentsSkinParams,
					themeFonts: themeRefs.themeFonts,
					compData: refs.dataQuery(component.dataQuery!),
			  },
	toViewItem: (__, deps) => {
		if (!deps || !deps.compStyles) {
			return null
		}

		const { compStyles, sitesEditorName, theme, experiments, siteColors, themeFonts, compData } = deps
		const isSiteFromEditor = sitesEditorName === 'Studio' || !!experiments['specs.thunderbolt.forceStudio']
		const isStyleOverrideGranularCompositionsOn = experiments['specs.thunderbolt.styleOverridesGranularConverters']
		if (compStyles.type === 'classic') {
			return {
				type: 'classic' as const,
				css: mapValues(compStyles.css, (css: Style) =>
					styleToCssVars(css, siteColors, isSiteFromEditor, theme?.spx, deps.componentsSkinParams)
				),
			}
		}

		const variantsCss = mapValuesValues(compStyles.css, (css: Style) => {
			if ((compData as WidgetRefDataItem)?.isBuilderComponent) {
				return isStyleOverrideGranularCompositionsOn
					? stylePropsToCssVars(css)
					: oldStylePropsToCssVars(css, themeFonts, isSiteFromEditor, theme?.spx)
			}

			return styleToCssVars(css, siteColors, isSiteFromEditor, theme?.spx, deps.componentsSkinParams)
		})
		return {
			type: 'variants' as const,
			css: variantsCss,
		}
	},
})

export const innerElementTheme = createCssCompNode('styleBased', 'innerElementTheme', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) => ({
		innerElementsStyles: component.innerElementsQuery ? refs.innerElementsStyles(component) : undefined,
		innerElementsSelectors: envRefs.selector(component.id),
		theme: themeRefs.theme,
		themeFonts: themeRefs.themeFonts,
		sitesEditorName: envRefs.editorName,
		experiments: envRefs.experiments,
	}),
	toViewItem: (__, deps) => {
		if (!deps || _.isEmpty(deps.innerElementsStyles)) {
			return null
		}

		const { innerElementsStyles, innerElementsSelectors, themeFonts, experiments, sitesEditorName, theme } = deps

		const isSiteFromEditor = sitesEditorName === 'Studio' || !!experiments['specs.thunderbolt.forceStudio']
		const isStyleOverrideGranularCompositionsOn = experiments['specs.thunderbolt.styleOverridesGranularConverters']

		const cssVars = _.reduce(
			innerElementsStyles,
			(result, value, key) => {
				const styles = _.reduce(
					value,
					(res, val, breakpoint) => {
						if (isStyleOverrideGranularCompositionsOn) {
							res[breakpoint] = stylePropsToCssVars(_.get(val, ['']))
						} else {
							res[breakpoint] = oldStylePropsToCssVars(
								_.get(val, ['']),
								themeFonts,
								isSiteFromEditor,
								theme?.spx
							)
						}
						return res
					},
					{} as { [breakpoint: string]: {} }
				)

				result[key] = styles
				return result
			},
			{} as { [innerElementId: string]: {} }
		)

		return {
			type: 'variants' as const,
			css: cssVars,
			innerElementsSelectors,
		}
	},
})

export const innerElementsStyles = createCssCompNode('styleBased', 'innerElementsStyles', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) =>
		component.innerElementsQuery ? refs.innerElementsQuery(component.innerElementsQuery) : undefined,
	toViewItem: (component, deps) => {
		if (!deps) {
			return null
		}

		const generateInnerElementsToStyleMap = (
			widgetDataItem: ResolvedBuilderWidgetDataItem,
			parentKey: string = ''
		): InnerElementsMap => {
			const result: InnerElementsMap = {}

			for (const innerElementName in widgetDataItem) {
				if (widgetDataItem[innerElementName] && typeof widgetDataItem[innerElementName] === 'object') {
					const combinedKey = parentKey ? `${parentKey}/${innerElementName}` : innerElementName

					if ('styleId' in widgetDataItem[innerElementName]) {
						result[combinedKey] = widgetDataItem[innerElementName]
					}

					if ('innerElementsQuery' in widgetDataItem[innerElementName]) {
						const nestedResult = generateInnerElementsToStyleMap(
							widgetDataItem[innerElementName].innerElementsQuery,
							combinedKey
						)
						Object.assign(result, nestedResult)
					}
				}
			}

			return result
		}

		const innerElements = generateInnerElementsToStyleMap(deps)

		return (_.mapValues(innerElements, (innerElement) => {
			const items = (isRefArray(innerElement.styleId)
				? innerElement.styleId.values
				: [innerElement.styleId || createEmptyStyleItem(component)]) as Iterable<
				VariantRelationViewItem<ResolvedInnerElementsDataItem> | ResolvedInnerElementsDataItem
			>

			return toBreakpointVariantsMap(items, (item) => item)
		}) as unknown) as {
			[innerElementId: string]: InnerElementStyle
		}
	},
})

const viewerCompThemeCompNode = createCssCompNode('styleBased', 'compTheme', {
	getDependencies: compThemeCompNode.getDependencies,
	toViewItem: (component, deps) => {
		const compTheme = compThemeCompNode.toViewItem(component, deps)
		if (!compTheme || compTheme.type === 'classic') {
			return compTheme
		}
		return {
			...compTheme,
			css: clearFutileOverridesFromCssObject(compTheme.css),
		}
	},
})

export const compTheme = (isEditor: boolean) => (isEditor ? compThemeCompNode : viewerCompThemeCompNode)
